/**
 * Contact Info Hook
 *
 * Reads country-specific contact information from ContactInfoProvider context
 * (seeded server-side in the locale layout).
 */

import { FALLBACK_CONTACT } from '@/lib/algolia/contact';
import { ContactInfoContext } from '@/providers/contactInfoProvider';
import { useContext } from 'react';

export interface ContactInfoResult {
  /** Contact phone number */
  phoneNumber: string;
  /** Contact email */
  email: string;
  /** Formatted weekday hours */
  weekdayHours: string;
  /** Formatted weekend hours */
  weekendHours: string;
  /** Current country code */
  countryCode: string | null;
  /** Whether data is being loaded */
  isLoading: boolean;
  /** Whether layout contact data is available */
  isInitialized: boolean;
  /** Error message if any */
  error: string | null;
  /** Phone number as tel: link */
  phoneHref: string;
  /** Email as mailto: link */
  emailHref: string;
}

/**
 * Hook to access contact information for the active locale market.
 */
export function useContactInfo(): ContactInfoResult {
  const contact = useContext(ContactInfoContext);
  const data = contact ?? FALLBACK_CONTACT;

  return {
    phoneNumber: data.phoneNumber,
    email: data.email,
    weekdayHours: data.weekdayHours,
    weekendHours: data.weekendHours,
    countryCode: contact?.countryCode ?? null,
    isLoading: false,
    isInitialized: contact !== null,
    error: null,
    phoneHref: `tel:${data.phoneNumber.replace(/\s/g, '')}`,
    emailHref: `mailto:${data.email}`,
  };
}
